import { SafeBaseObject } from "@app/core/models/base-object.models";
import { DateTime } from "luxon";
import { ResBooking } from "./res-booking.model";
import { getRootInjector } from '@app/core/injector/root-injector';
import { CoreFormatService } from "@app/core/services/core-format.service";
import { IReservationApiCreateCashReceipt } from "./res-cash-receipt.model";

/**
 * Possible checkout layout
 *
 * CRYSTAL (default)
 * AANDK.UK: Abercrombie & Kent
 *
 */
type ResPaymentCheckoutLayout = 'CRYSTAL' | 'AANDK.UK' | 'PHILANTROPY' | 'SANCTUARYPA';

export enum ResRequestPaymentStatus {
  OK = "OK",
  KO = "KO",
  In_Progress = "PR", // Payment in progress
  Expired = "EX",
  Refunded = "RF",
  Unreferenced_Refunded = "UR",
  Pending = "PN", // No answer from payment iframe
  Aborted = "AB", // Go back: Payment iframe closed
  Voided = "VD", // OK but further voided
  Error = 'ER' //Error in procedure
}

export const PAYMENT_RESULTS_MESSAGE: { [key: string]: string } = {
  OkPayEn: "'The payment has been successfully submitted. Check status on history view.",
  OK: "The payment has been confirmed.",
  Error: "An error is occurred in payment request, please retry.", //Error in procedure
  AbortedByUser: "The payment has been canceled by user.", // Go back: Payment iframe closed
  AbortedByTimeout: "The payment has been canceled due to operation timeout.", // Go back: Timeout expired
  Voided: "The payment has been voided." // OK but further voided -> WTF?
}

export interface IResPaymentResultEvent {
  status: ResRequestPaymentStatus,
  message?: string
}

/**
 * After payment redirection status
 * AKA Possible values of p/P parameter in checkout/payment page
 *
 * @export
 * @enum {number}
 */
export enum ResPaymentRedirectionStatus {
  KO = "0",
  OK = "1"
}

/**
 * Payment request info response from API
 *
 * @export
 * @interface IResPaymentApiRequestInfo
 */
export interface IResPaymentApiRequestInfo {
  Info: Array<{
    Amount: number;
    BkgID: number;
    CurrencyCod: string;
    ItemName: string;
    PayGUID: string;
    RequestID: string;
    VoaygeID: string;
    Result: string;
    Message: string;
    Description?: string;
    IsPostponed?: boolean;
    IsFloatingDeposit?: boolean;
    OrderID?: string;
    SendPaymentEmail?: string;
    Status?: string;
    Url?: string;
    CheckoutLayout?: ResPaymentCheckoutLayout
  }>;
  HasError: boolean;
}
export interface IResPaymentApiEnquireInfo {
  result: string;
  message: string;
  status: string;
  hasError: boolean;
}
export interface IResPreAuthResponse {
  url: string;
  mode: string
  result: string;
  message: string;
  isPosponed:boolean
}
/**
 * Payment request response from API
 *
 * @export
 * @interface IResPaymentApiRequest
 * @extends {IResPaymentApiRequestInfo}
 */
export interface IResPaymentApiRequest extends IResPaymentApiRequestInfo {
  /**
   * Checkout layout
   *
   * @type {ResPaymentCheckoutLayout}
   * @memberof IResPaymentApiRequest
   */
  CheckoutLayout?: ResPaymentCheckoutLayout
  /**
   * Details to build the form to pay in a new page
   *
   * @type {{
   *     Url: string;
   *     Fields: { [key: string]: string };
   *     Method?: string;
   *     RequestID: string;
   *   }}
   * @memberof IResPaymentApiRequest
   */
  FormModel?: {
    Url: string;
    Fields: { [key: string]: string };
    Method?: string;
    RequestID: string;
  };
  /**
   * Details to build iframe to pay in iframe
   *
   * @type {{
   *     Url: string;
   *   }}
   * @memberof IResPaymentApiRequest
   */
  IframeModel?: {
    Url: string;
    ForceSelfPage?: string
  }
}

/**
 * Payment request confirmation model
 * (used for postponed payment)
 *
 * @export
 * @interface IResPaymentRequestConfirmation
 */
export interface IResPaymentRequestConfirmation {
  /**
   * Payment request ID
   *
   * @type {string}
   * @memberof IResPaymentRequestConfirmation
   */
  requestID: string;
  /**
   * Payment request result
   *
   * @type {"OK" | "WRN" | "ERR"}
   * @memberof IResPaymentRequestConfirmation
   */
  result: "OK" | "WRN" | "ERR" | "RUN";
  /**
   * Payment mode
   *
   * @type {"form" | "iframe" | "_self" | "_blank" | "unknown" }
  * @memberof IResPaymentRequestConfirmation
  */
  mode?: "form" | "iframe" | "_self" | "_blank" | "unknown";
  url?: string;
  /**
   * Booking ID
   *
   * @type {number}
   * @memberof IResPaymentRequestConfirmation
   */
  bkgID: number;
  /**
   * Warning or Error message
   *
   * @type {string}
   * @memberof IResPaymentRequestConfirmation
   */
  message: string;
  /**
   * Is the payment postponed flag
   *
   * @type {boolean}
   * @memberof IResPaymentRequestConfirmation
   */
  isPostponed: boolean;
  /**
   * Checkout page layout
   *
   * @type {ResPaymentCheckoutLayout}
   * @memberof IResPaymentRequest
   */
  checkoutLayout?: ResPaymentCheckoutLayout;
}

/**
 * Payment request model
 *
 * @export
 * @interface IResPaymentRequest
 */
export interface IResPaymentRequest extends IResPaymentRequestConfirmation {
  /**
   * URL to be placed in
   * - iframe when mode == "iframe"
   * - payment form when mode == "form"
   *
   * @type {string}
   * @memberof IResPaymentRequest
   */
  url: string;
  /**
   * Payment amount
   *
   * @type {number}
   * @memberof IResPaymentRequest
   */
  amount: number;
  /**
   * Payment currency
   *
   * @type {string}
   * @memberof IResPaymentRequest
   */
  currency?: string;
  /**
   * Payment mode
   *
   * @type {"form" | "iframe" | "unknown"
   * }
   * @memberof IResPaymentRequest
   */
  mode: "form" | "iframe" | "_self" | "_blank"| "unknown";
  /**
   * Fields to be placed in payment form when mode == "form"
   *
   * @type {{ key: string, value: string }[]}
   * @memberof IResPaymentRequest
   */
  fields?: { key: string, value: string }[];
  /**
   * Method to be placed in payment form when mode == "form"
   *
   * @type {string}
   * @memberof IResPaymentRequest
   */
  method?: string;
}

/**
 * Payment request parameters
 *
 * @export
 * @interface IResPaymentRequestParams
 */
export interface IResPaymentRequestParams {
  /**
   * Booking ID
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  BkgID: number;
  /**
   * Booking voyage codes in format "..., ..."
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  Voyage: string;
  /**
   * Payer first name
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CardHldName?: string,
  /**
   * Payer last name
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CardHldLastName?: string,
  /**
   * Payer email
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CardHldEmail?: string,
  /**
   * Payer address first line
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CardHldAddress1?: string,
  /**
   * Payer address second line
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CardHldAddress2?: string,
  /**
   * Payer city
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CardHldCity?: string,
  /**
   * Payer ZIP code
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CardHldPostalCode?: string,
  /**
   * Payer state
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CardHldState?: string,
  /**
   * Payer country code
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CardHldCountry?: string,
  /**
   * Payer birth date
   *
   * @type {DateTime}
   * @memberof IResPaymentRequestParams
   */
  CardHldBirthDate?: DateTime,
  /**
   * Payer phone
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CardHldPhone?: string,
  /**
   * Currency
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CurrencyCod: string,
  /**
   * Payment amount
   *
   * @type {number}
   * @memberof IResPaymentRequestParams
   */
  Amount: number,
  /**
   * The payment will be in iframe flag
   *
   * @type {boolean}
   * @memberof IResPaymentRequestParams
   */
  PaymentInIframe?: boolean,
  /**
   * Email to send the payment link
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  SendPaymentEmail: string,
  /**
   * Postponed payment flag
   *
   * @type {boolean}
   * @memberof IResPaymentRequestParams
   */
  PostponedPayment?: boolean;
  /**
   * Keep data (tokenize card) flag
   *
   * @type {boolean}
   * @memberof IResPaymentRequestParams
   */
  KeepData: boolean | null | undefined;
  /**
   * Payment status
   *
   * @type {ResRequestPaymentStatus}
   * @memberof IResPaymentRequestParams
   */
  Status?: ResRequestPaymentStatus;
  /**
   * Payment response
   *
   * @type {ResRequestPaymentStatus}
   * @memberof IResPaymentRequestParams
   */
  Response?: ResRequestPaymentStatus;
  /**
   * Enable send CUSTOM FIELD to gateway (reuqire by amex)
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  UseAmexData?:boolean;
  /**
   * Passenger name (AMEX reuired)
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
   PassengerName?: string;
  /**
   * Departure Date (AMEX reuired: YYYMMDD)
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  DepartureDate?: string;
  /**
   * Passanger name (AMEX reuired)
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
   ArrivalDate?: string;
  /**
   * Cruise Ship name (AMEX reuired)
   *
   * @type {string}
   * @memberof IResPaymentRequestParams
   */
  CruiseShipName?: string;
  PayerGroupID?: number;
  ReasonCode: string;
  PaxGroupID?: number;
  PayerID?: number;
  OtherCurrency?: string;
  ReceiptTotalOtherCurr?: number;
  FxRate?: number;
  // Markup?: number;
  // MarkupValue?: number
  ReceiptTotal?: number;
  ReceiptCurrency?: string;
  ItemListID?: number;
  TestError?: boolean | null | undefined
  RequestDetail?:any[]
  Notes?: string
}
/**
 * Interface of a payment action (repay/refund/void) params
 *
 * @export
 * @interface IResPaymentActionParams
 */
export interface IResPaymentActionParams {
  OrderID: string;
  Reason: string;
  Amount?: number;
  Data?: string;
  BkgId?: number;
  OtherCurrency?: string;
  ReceiptTotalOtherCurr?: number;
  FxRate?: number;
  Markup?: number;
  MarkupValue?: number;
  ReceiptTotal?: number;
  ReceiptCurrency?: string;
  IndividualID?: number;
  ReceiptTypeID?: number;
  CashAccount?: number;
  DebitCredit?: string;
  RequestDetail?:any[]
}
export interface IResPreAuthAction {
  UseToken:number
  BkgID?: number;
  OrderID?: string;
  Reason: string;
  Amount: number;
  ExpiredDate?: DateTime;
  Currency: string;
  PreAuthPropertyID: number
}



/**
 * Interface of transfer cash between bookings
 *
 * @export
 * @interface IResTransferCashBookingParams
 */
export interface IResTransferCashBookingParams {
  BkgIDFrom: number;
  GroupIDFrom?: number
  PaymentCodFrom?: string,
  BkgIDTo?: number;
  GroupIDTo?: number;
  Amount: number;
  CertificateID?: number;
  Insurance?: 'Y';
  PaymentCodTo?: string;
}
/**
 * Interface of transfer cash between groups
 *
 * @export
 * @interface IResTransferCashGroupParams
 */
// export interface IResTransferCashGroupParams {
//   GrpIDFrom?: number;
//   GrpIDTo?: number;
//   Amount: number;
// }
export type IResTransferCashParams = IResTransferCashBookingParams; // | IResTransferCashGroupParams;

/**
 * Reservation payment model
 *
 * @export
 * @class ResPayment
 * @extends {SafeBaseObject}
 */
export class ResPayment extends SafeBaseObject {

  public static INVALID_PAYMENT_ORDERID = '-1';

  public static INVALID_PAYMENT_REQUEST(): IResPaymentRequest {
    return {
      result: 'ERR',
      bkgID: ResBooking.INVALID_BOOKING,
      requestID: '',
      message: '',
      isPostponed: false,
      mode: 'unknown',
      url: '',
      amount: 0
    }
  };

  public amount: number = 0;
  public bkgID: number = -1;
  public orderID = ResPayment.INVALID_PAYMENT_ORDERID;
  public cardType: string = "";
  public commission: boolean = false;
  public creditCardTypeID: number = -1;
  public currencyCod: string = "";
  public isCreditCard: boolean = false;
  public linkPayment: boolean = false;
  public linkExpireDate: DateTime = DateTime.min();
  public payerName: string = "";
  public paymentTransactionStatus: string = "";
  public cardNumber: string = '';
  public reason: string = "";
  public receiptCreditCardAuthorisation: number = -1;
  public receiptCreditCardEndDate: DateTime = DateTime.min();
  public receiptCreditCardIssue: DateTime = DateTime.min();
  public receiptCreditCardNameOnCard: string = "";
  public receiptCreditCardStartDate: DateTime = DateTime.min();
  public receiptCreditCardValidation: number = -1;
  public receiptDate: DateTime = DateTime.min();
  public receiptDescription: string = "";
  public receiptID: number = -1;
  public receiptNumber: number = -1;
  public receiptTotal: number = -1;
  public receiptTypeID: number = -1;
  fxRate = 1;
  otherCurrency = ''
  receiptTotalOtherCurr = -1;
  receiptTotalOtherCurrFormatted = ''
  public receiptTypeName: string = "";
  public sectionID: number = -1;
  public sendPaymentEmail: string = "";
  public url: string = "";
  public refundAvailable?: boolean;
  public voidAvailable?: boolean;
  public repayAvailable?: boolean;
  public maxRefundAmount: number = 0;
  maxRefundAmountOtherCurr = 0
  public manualRefundAvailable?: string = "";
  public canCommissionRefund?: string = "";
  public unreferencedRefundAvailable:boolean = false;
  public mouseOverDescription:string = "";
  isPreAuth = false
  requestPaymentActionID = 0
  notes = ''
  paxGroupIDs = ''
  username = ''
  paymentRef = ''

  get usedOtherCurrency() {
    return (this.otherCurrency ?? '') !== '' && this.currencyCod !== this.otherCurrency
  }
  public get mappedPaymentTransactionStatus(): string {
    if (["UR", "RF", "VD"].includes(this.paymentTransactionStatus)) {
      return "OK";
    }

    const key = Object.keys(ResRequestPaymentStatus).find(x => (<any>ResRequestPaymentStatus)[x] == this.paymentTransactionStatus);
    return key?.replace('_', ' ') ?? "";
  }

  public get isValidForRefund(): boolean {
    return this.orderID !== ResPayment.INVALID_PAYMENT_ORDERID && !!this.refundAvailable;
  }

  public get isValidForVoid(): boolean {
    return this.orderID !== ResPayment.INVALID_PAYMENT_ORDERID && !!this.voidAvailable;
  }

  public get isValidForRepay(): boolean {
    return this.orderID !== ResPayment.INVALID_PAYMENT_ORDERID && !!this.repayAvailable;
  }

  public get isValidForUnrefRefund(): boolean {
    return this.orderID !== ResPayment.INVALID_PAYMENT_ORDERID && this.unreferencedRefundAvailable;
  }


  public get reasonOrDesc(): string {
    return this.receiptDescription ?? this.reason;
  }
  formatService:CoreFormatService
  constructor(data?: IReservationApiPayment) {
    super();
    this.formatService = getRootInjector(CoreFormatService);
    if (data) {
      this.updateData(data);
    }
  }

  public override updateData(data: IReservationApiPayment): void {
    this.addMangledProperty(data);

    this.addDateTimeProperty("receiptCreditCardEndDate", data, "ReceiptCreditCardEndDate");
    this.addDateTimeProperty("receiptCreditCardIssue", data, "ReceiptCreditCardIssue");
    this.addDateTimeProperty("receiptCreditCardStartDate", data, "ReceiptCreditCardStartDate");
    this.addDateTimeProperty("receiptDate", data, "ReceiptDate");
    this.addDateTimeProperty("linkExpireDate", data, "LinkExpireDate");

    this.addBooleanProperty("commission", data, "Commission");
    this.addBooleanProperty("iSCreditCard", data, "IsCreditCard");
    this.addBooleanProperty("linkPayment", data, "LinkPayment");
    this.addBooleanProperty("refundAvailable", data, "RefundAvailable");
    this.addBooleanProperty("voidAvailable", data, "VoidAvailable");
    this.addBooleanProperty("repayAvailable", data, "RepayAvailable");

    this.receiptTotalOtherCurrFormatted = this.receiptTotalOtherCurr === -1 ? '' : this.formatService.CurrencyAmount(this.receiptTotalOtherCurr, this.otherCurrency)
  }
}
export class ResManualReceipt extends SafeBaseObject {
  batchID = 1
  bkgID = 0
  receiptDate? = DateTime.invalid('empty')
  amount = 0
  currencyCod = ''
  receiptTypeID = 0
  glAccountID = 0
  cashAccountCode = ''
  checkNo = ''
  reference = ''
  payerGroupID = 0
  paxGroupID = 0
  reason = ''
  emailTo = ''
  sequence = 1
  result = ''
  paymentTypeCod = 'P'
  individualID = 0
  mihMaxRefund = 0
  paymentNumber = 0

  constructor(data?:any){
    super()
    this.updateData(data)
  }
  override updateData(data?:any) {
    if(!data) return
    super.updateData(data)
  }
  static createFromBooking(booking:ResBooking) {
    const mr = new ResManualReceipt()
    mr.bkgID = booking.bkgID
    mr.receiptDate = DateTime.now()
    mr.currencyCod = booking.bkgCurrency
    return mr
  }
  get batchAmount(){
    return (this.paymentTypeCod==='R' ? -1:1) * this.amount
  }
  isValid() {
    return this.bkgID>0
        && this.amount > 0
        && this.currencyCod !== ''
        && this.cashAccountCode !== ''
        // && this.payerGroupID > 0 // CB: Removed check for Agent payer (-10)
        && this.receiptDate?.isValid
        && (!this.isMoneyInHouse || this.individualID)
  }
  get isMoneyInHouse(): boolean {
    return this.receiptTypeID === 56
  }
}
export class ResNewPayment extends SafeBaseObject {
  bkgID = 0
  paymentDueDate = DateTime.invalid('empty')
  amount = 0
  isFinalPayment = false
  hasInsurance	= false
  reason = ''
  reasonCode = ''
  payer = new ResPaymentPayer()
  isSelected = false
  isDefault = false
  payerGroupID = 1
  payerGroupName = ''
  payerGroupType = ''
  paxGroupID = 1
  paymentLink = ''
  itemListID = 0

  constructor(data?:any){
    super()
    this.updateData(data)
  }
  override updateData(data?:any) {
    if(!data) return
    super.updateData(data)
  }
}
export class ResPaymentPayer extends SafeBaseObject {
  payerGroupID = 0
  payerGroupName = ''
  payerGroupType = ''

  paxGroupID = 0
  itemListID = 0

  lastName = ''
  firstName = ''
  email = ''
  birthDate = DateTime.invalid('empty')
  address1 = ''
  address2 = ''
  phone = ''
  zip1 = ''
  city = ''
  countryCod = ''
  stateCod = ''

  constructor(data?:any){
    super()
    this.updateData(data)
  }
  override updateData(data?:any) {
    if(!data) return
    super.updateData(data)
  }
}
export class ResPaymentRefundInfo extends SafeBaseObject {
  bkgID = 0
  orderID = 0
  requestID = ''
  currencyCod = ''
  orderAmount = 0
  orderRefunded = 0
  orderRemaining = 0
  paymentProvider = ''
  requestStatus = ''
  details:ResPaymentRefundInfoDetails[] = []
  groups:ResPaymentRefundInfoGroup[] = []
  total = 0

  get isValid() {
    return this.bkgID > 0 && this.details.length > 0
  }

  constructor(data?:any){
    super()
    this.updateData(data)
  }
  override updateData(data?:any) {
    if(!data) return
    super.updateData(data)

    if (data.Details) this.details = data.Details.map((item: any) => new ResPaymentRefundInfoDetails(item))

    this.details
        .forEach(d=>{
          let group = this.groups.find(g=>g.groupID===d.paxGroupID)

          if(!group) {
            group = new ResPaymentRefundInfoGroup({groupID: d.paxGroupID})
            this.groups.push(group)
          }

          group.lines.push(d)
        })
  }
}
export class ResPaymentRefundInfoGroup extends SafeBaseObject {
  groupID = 0
  name = ''

  collapsed = false
  amount = 0
  lines:ResPaymentRefundInfoDetails[]=[]

  constructor(data?:any){
    super()
    this.updateData(data)
  }
  override updateData(data?:any) {
    if(!data) return
    super.updateData(data)
    this.name = `Group ${this.groupID}`
  }
}
export class ResPaymentRefundInfoDetails extends SafeBaseObject {
  paxGroupID = 0
  paymentCod = ''
  paidAmount = 0
  alreadyRefunded = 0
  sliceRemaining = 0
  groupRefundable = 0
  orderRemaining = 0
  maxRefundable = 0
  defaultAmount = 0
  paymentDesc = ''

  amount = 0
  maxAmount = 0
  selected:boolean | null | undefined = false
  key = ''

  constructor(data?:any){
    super()
    this.updateData(data)
  }
  override updateData(data?:any) {
    if(!data) return
    super.updateData(data)
    this.key = `${this.paxGroupID}-${this.paymentCod}`
    this.amount = this.defaultAmount
    this.maxAmount = this.maxRefundable
    this.selected = this.defaultAmount > 0
  }
}
export class ResPaymentCurrency extends SafeBaseObject  {
  paymentProvider = ''
  paymentProviderType = ''
  otherCurrency = ''
  fxRate = 1

  constructor(data?:any){
    super()
    this.updateData(data)
  }
  override updateData(data?:any) {
    if(!data) return
    super.updateData(data)
  }
}

export class ResPaymentCertificate extends SafeBaseObject  {
  certificateID = 0
  certificateTypeDesc = ''
  maxAmountToTransfer = 0
  amount = 0
  description = ''
  amountUsed = 0

  constructor(data?:any){
    super()
    this.updateData(data)
  }
  override updateData(data?:any) {
    if(!data) return
    super.updateData(data)
    this.description = `${this.certificateTypeDesc} Max Amount:${this.maxAmountToTransfer} (${this.certificateID})`
  }
}

/**
 * Reservation payment API model
 *
 * @export
 * @interface IReservationApiPayment
 */
export interface IReservationApiPayment {
  BkgID?: number;
  CardType?: string;
  Commission?: string;
  CreditCardTypeID?: number;
  CurrencyCod?: string;
  Guid?: string;
  IsCreditCard?: string;
  LinkPayment?: string;
  PayerName?: string;
  CardNumber?: string
  PaymentTransactionStatus?: string;
  ReceiptCreditCard?: number;
  ReceiptCreditCardAuthorisation?: number;
  ReceiptCreditCardEndDate?: string;
  ReceiptCreditCardIssue?: string;
  ReceiptCreditCardNameOnCard?: string;
  ReceiptCreditCardStartDate?: string;
  ReceiptCreditCardValidation?: number;
  ReceiptDate?: string;
  ReceiptDescription?: string;
  ReceiptID?: number;
  ReceiptNumber?: number;
  ReceiptTotal?: number;
  ReceiptTypeID?: number;
  ReceiptTypeName?: string;
  SectionID?: number;
  SendPaymentEmail?: string;
  Url?: string;
  RefundAvailable?: string;
  VoidAvailable?: string;
  RepayAvailable?: string;
  Reason?: string;
  ReceiptTime?: string;
  RequestID?: string;
  Amount?: number;
  LinkExpireDate?: string;
  OrderID?: number;
  MaxRefundAmount?: number;
  MouseOverDescription?: string;
}

export class ResPaymentScheduleGroup extends SafeBaseObject {
  paymentScheduleId = 0
  grpId = 0
  paymentDate = DateTime.invalid('empty')
  paymentNumber = 0
  amount = 0
  currencyCod = ''
  note = ''
  formattedPaymentDate = ''

  constructor(data?: any) {
    super()
    this.updateData(data)
    this.formattedPaymentDate = `${this.paymentDate.toFormat('dd MMM yyyy')} - ${this.note}`
  }
}

export interface IReservationApiCreateCashReceiptParams {
  PaymentDate?: DateTime
  CashReceipt: IReservationApiCreateCashReceipt[]
  IndividualID?: number
  PayerID?: number
  PaymentNumber?: number
  GrpID?: number
  RequestDetail?:any[]
}

export class ResBookingPayerMngmtPayers extends SafeBaseObject {
  recordType = ''
  payerID = 0
  payerName = ''
  payerRoleID = 0
  payerRole = ''
  individualID = 0
  paymentDepartmentID = 0
  guestID = 0
  guestName = ''
  payForGroup = false
  payForYourSelf = false

  guestByGroup:{ paxGroupID: number, guests: ResBookingPayerMngmtGuests[] }[] = []

  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}

export class ResBookingPayerMngmtGroups extends SafeBaseObject {
  paxGroupID = 0
  payerID = 0
  payerName = ''
  isGroupDefault = false

  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}

export class ResBookingPayerMngmtGuests extends SafeBaseObject {
  guestID = 0
  guestCod = ''
  guestName = ''
  paxGroupID = 0
  effectivePayerID = 0
  explicitPayerID = 0
  payerSource = ''
  payerName = ''

  virtual = false
  originalPayerID = 0

  constructor(data?:any){
    super()
    this.updateData(data)
  }
  override updateData(data?:any) {
    if(!data) return
    super.updateData(data)
    this.originalPayerID = this.effectivePayerID
  }
}
export class ResBookingPayerMngmtRelations extends SafeBaseObject {
  paxGroupID = 0
  payerID = 0
  guestID = 0

  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}
export class ResBookingPayerMngmtRoles extends SafeBaseObject {
  payerRoleID = 0
  payerRoleName = ''

  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}

export interface IResApiManageBookingPayerManagementParams {
  BkgID: number
  Payer: IResBookingPayerMngmtPayers[]
  PayerAssociation: IResBookingPayerMngmtPayersAssociation[]
  PayerRemap: IResBookingPayerMngmtPayersRemap[]
}
export class ResMaganeBookingPayerResponse extends SafeBaseObject {
  tempPayerID = 0
  payerID = 0

  constructor(data?:any){
    super()
    this.updateData(data)
  }

  override updateData(data?:any) {
    if(!data) return
    super.updateData(data)
  }
}

export interface IResBookingPayerMngmtPayers {
  PayerID: number,
  PayerRoleID: number,
  IndividualID?: number,
  PaymentDepartmentID?: number,
}

export interface IResBookingPayerMngmtPayersAssociation {
  PayerID: number,
  TargetLevel: 'GROUP' | 'GROUP_PAYER' | 'GUEST',
  TargetID: number,
}
export interface IResBookingPayerMngmtPayersRemap {
  GuestID: number
  PaxGroupID: number
  FromPayerID: number
  ToPayerID: number
}
export class ResBookingPayerMngmtGroupGuests extends SafeBaseObject {
  paxGroupID = 0
  payerID = 0
  guestID = 0

  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}
export class ResBookingPaymentDetails extends SafeBaseObject {
  bkgID= 0
  bkgName= ''
  currencyCod= ''
  companyID= 0
  orderID= 0
  requestID= 0
  totalRequestAmount= 0
  requestStatus= ''
  created= DateTime.invalid('empty')
  payerID= 0
  payerName= ''
  individualID = 0
  cardHldName= ''
  cardHldLastName= ''
  cardHldAddress1= ''
  cardHldAddress2= ''
  cardHldCity= ''
  cardHldState= ''
  cardHldPostalCode= ''
  cardHldCountry= ''
  cardHldEmail=''
  tokenCreationRequested = false
  fxRate= 0
  otherCurrency= ''
  multiCurrencyFlag= false
  receiptTotalOtherCurr= 0
  markup= 0
  markupValue= 0
  paymentProvider= ''
  paymentProviderType= ''
  isPreAuth = false
  payers: ResBookingPaymentDetailPayer[] = []
  constructor(data?: any) {
    super()
    this.updateData(data)
    if (data.Payers) this.payers = data.Payers.map((item: any) => new ResBookingPaymentDetailPayer(item))
  }
}
export class ResBookingPaymentDetailPayer extends SafeBaseObject {
  paxGroupID = 0
  payerID = 0
  payerName = ''
  paymentCod = ''
  paymentDesc = ''
  paymentAmount = 0
  paymentCurrency = ''
  fxRate = 0
  bkgCurrency = ''
  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}
export class ResBookingPayerDetails extends SafeBaseObject {
  itemType = ''
  itemID = 0
  bkgID = 0
  paxGroupID = 0
  guestCod = 0
  guestID = 0
  guestName = ''
  itineraryDate = DateTime.invalid('empty')
  itemDescription = ''
  sellingPrice = 0
  sellingPriceCurrency = ''
  isIncluded = false
  effectivePayerID = 0
  explicitPayerID = 0
  payerSource = ''
  payerName = ''
  canChangePayer = false

  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}
export class ResBookingPayerGroup extends SafeBaseObject {
  paxGroupID = 0
  payerID = 0
  payerName = ''

  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}

// New payment page
export class ResPaymentInfo {
  header = new ResPaymentNewHeader()
  payers:ResPaymentNewPayer[] = []
  groups:ResPaymentNewGroup[] = []
  payerGroups:ResPaymentNewPayerGroup[] = []
  currencies:ResPaymentNewCurrency[] = []
  paymentDetails:ResPaymentNewPaymentDetail[] = []
}
export class ResPaymentNewHeader extends SafeBaseObject {
  bkgID = 0
  bkgName = ''
  currencyCod = ''
  currencySymbol = ''
  groupsCount = 0
  backgroundColor = ''
  foregroundColor = ''
  companyID = 0
  logo = ''
  companyType = ''
  travelDate = DateTime.invalid('empty')
  otherCurrencyEnabled = false

  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}

export class ResPaymentNewPayer extends SafeBaseObject {
  payerID = 0
  payerName = ''
  individualID = 0
  cardHldName = ''
  cardHldLastName = ''
  cardHldCountry = ''
  cardHldEmail = ''
  cardHldBirthDate? = DateTime.invalid('empty')
  cardHldPhone = ''
  cardHldAddress1 = ''
  cardHldAddress2 = ''
  cardHldPostalCode = ''
  cardHldCity = ''
  cardHldState = ''

  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}

export class ResPaymentNewGroup extends SafeBaseObject {
  groupID = 0
  groupName = ''
  groupGuests = ''

  subTotalFmt = ''
  subTotal = 0

  visible = true
  enabled: boolean | null | undefined  = true
  selected: boolean | null | undefined = true
  disableMessage = ''
  collapsed = false
  amountType: 'manual' | 'auto' = 'auto'

  lines:ResPaymentNewPaymentDetail[] =[]
  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}

export class ResPaymentNewPayerGroup extends SafeBaseObject {
  payerID = 0
  groupID = 0

  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}

export class ResPaymentNewCurrency extends SafeBaseObject {
  paymentProvider = ''
  paymentProviderType = ''
  otherCurrency = ''
  fxRate = 0

  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}

export class ResPaymentNewPaymentDetail extends SafeBaseObject {
  groupID = 0
  payerID = 0
  paymentCod = ''
  paymentDesc = ''
  paymentAmount = 0
  paymentCategoryCod = ''
  manualAmount = 0
  dueDate = DateTime.invalid('empty')
  isPaymentPlaceholder = false
  enabled = true
  visible = false
  selected: boolean | null | undefined = false
  disableMessage = ''

  get isInsurance() {
    return this.paymentCategoryCod === 'INS'
  }
  get isCancelInsurance() {
    return this.paymentCod === 'CAT'
  }
  get isDeposit() {
    return this.paymentCod === 'DEP'
  }
  get isFinalPayment() {
    return this.paymentCod === 'FIN'
  }
  constructor(data?: any) {
    super()
    this.updateData(data)
  }
}
